<script setup lang="ts">
import Dialog from 'primevue/dialog';
import { GenericObject } from 'vee-validate';
import { computed, inject, ref, shallowRef } from 'vue';

import { useWangsVueConfig } from 'lib/plugins/WangsVue';

import Button from '../button/Button.vue';
import Checkbox from '../checkbox/Checkbox.vue';
import {
  DialogFormEmits,
  DialogFormProps,
  DialogFormSlots,
} from '../dialogform/DialogForm.vue.d';
import Form from '../form/Form.vue';
import FormInstance, { FormPayload } from '../form/Form.vue.d';
import Icon from '../icon/Icon.vue';
import ValidatorMessage from '../validatormessage/ValidatorMessage.vue';

const props = withDefaults(defineProps<DialogFormProps>(), {
  asideRightWidth: 260,
  closable: true,
  closeOnSubmit: true,
  columnPerRow: 1,
  resetAfterSubmit: true,
  showStayCheckbox: false,
  buttonsTemplate: () => [],
  width: 'small',
});

const emit = defineEmits<DialogFormEmits>();
const slots = defineSlots<DialogFormSlots>();

const { locale } = useWangsVueConfig('DialogForm');

const DialogPreset = inject<Record<string, any>>('preset', {}).dialog;
const DialogFormPreset = inject<Record<string, any>>('preset', {}).dialogform;

const form = ref<FormInstance>();
const fieldsKey = shallowRef<number>(0);
const showConfirm = shallowRef<boolean>(false);
const expanded = shallowRef<boolean>(false);
const formPayload = ref<FormPayload>({
  stayAfterSubmit: false,
  formValues: {},
});

const computedWidth = computed(() => {
  const asideRightWidth: number =
    slots['aside-right'] && expanded.value ? props.asideRightWidth : 0;
  const baseWidth = {
    'small': 400,
    'medium': 500,
    'large': 572,
    'semi-xlarge': 600,
    'xlarge': 800,
  }[props.width];

  return `calc(${baseWidth}px + ${asideRightWidth}px)`;
});

const closeDialog = (): void => {
  emit('update:visible', false);
  emit('close');
};

/**
 * Callback function for submit button click.
 */
const onButtonSubmitClicked = (): void => {
  if (form.value && !props.invalid && !props.validatorMessage) {
    form.value.onSubmitClicked();
    form.value.submit();
  }
};

/**
 * Callback function for clear button click.
 *
 * @returns {void}
 */
const clearField = (): void => {
  if (form.value) {
    form.value.clearField();
  }

  emit('clear');
};

/**
 * Callback function for Form component's submit event.
 * Once all fields within Form component is valid, runs the aditional validation.
 *
 * The validation should change the invalid state.
 *
 * Once the invalid state become false, the emit 'submit' will be fired and this callback will be executed.
 *
 * @param {FormPayload} formPayload - The payload object containing the form data and options.
 * @returns {void}
 */
const onSubmitDialogForm = async (payload: FormPayload): Promise<void> => {
  if (props.validationFunction) {
    /**
     * This function will change the props.invalid value that will determine the next step.
     */
    await props.validationFunction(payload.formValues);
  }

  /**
   * Prevent submit callback executed if the form is still invalid.
   */
  if (!props.invalid && !props.validatorMessage) {
    formPayload.value = payload;

    if (slots['confirm']) showConfirm.value = true;
    else onFormValidated();
  }
};

const onFormValidated = async (
  submitFunction?: (value: FormPayload) => Promise<void> | void,
): Promise<void> => {
  const { stayAfterSubmit } = formPayload.value;

  /**
   * Only close dialog if the form valid and stayAfterSubmit not checked.
   */
  if (!stayAfterSubmit && props.closeOnSubmit) {
    emit('update:visible', false);
  }

  /**
   * If using confirmation on submit, the form should not be resets on submit clicked.
   * Resets the form if user confirmed the Confirm Dialog.
   *
   * The clear field should await the submitFunction succed.
   */
  if (slots['confirm']) {
    try {
      await submitFunction?.(formPayload.value);
    } catch (error) {
      console.error(error);
    }
  } else {
    emit('submit', formPayload.value);
  }

  if (props.resetAfterSubmit) clearField();
};

defineExpose({
  form,
  clearField,
  setValues: (values: GenericObject) => {
    form.value?.setValues(values);
  },

  setFieldValue: <T extends string>(
    field: T,
    value: GenericObject[T],
    shouldValidate?: boolean,
  ) => {
    form.value?.setFieldValue(field, value, shouldValidate);
  },

  setErrors: (fields: GenericObject) => {
    form.value?.setErrors(fields);
  },
});
</script>

<template>
  <Dialog
    v-bind="DialogFormPreset?.root"
    :class="props.class"
    :closable="closable"
    :draggable="false"
    :header="header"
    :pt="{
      root: {
        class: [
          ...DialogPreset?.root({ state: {} }).class,
          ...DialogFormPreset?.root.class,
        ],
        style: DialogFormPreset?.root.style(computedWidth),
      },
    }"
    :visible="visible"
    @hide="emit('hide')"
    @show="emit('show')"
    @update:visible="emit('update:visible', !!$event)"
    modal
  >
    <template #container>
      <div v-bind="DialogFormPreset?.container(expanded)">
        <section v-bind="DialogFormPreset?.mainsection" id="main">
          <div v-bind="DialogFormPreset?.header">
            <slot name="header">
              <Icon
                v-if="headerIcon"
                v-bind="DialogFormPreset?.headericon"
                :icon="headerIcon"
                :severity="severity"
                aria-label="Header Icon"
              />

              <h3 v-bind="DialogFormPreset?.headertitle">
                {{ header }}
              </h3>

              <Button
                v-if="$slots['aside-right']"
                v-bind="DialogFormPreset?.expandasidebutton"
                :label="
                  expanded
                    ? locale.asideRightExpandedButtonLabel
                    : locale.asideRightCollapsedButtonLabel
                "
                @click="expanded = !expanded"
                text
              />

              <Button
                v-if="closable"
                @click="closeDialog"
                v-bind="DialogFormPreset?.closedialog"
                icon="close"
                icon-class="w-[22px] h-[22px]"
                rounded
                severity="secondary"
                text
              />
            </slot>
          </div>

          <Form
            v-bind="DialogFormPreset?.form"
            ref="form"
            :buttons-template="buttonsTemplate"
            :column-per-row="columnPerRow"
            :reset-after-submit="resetAfterSubmit"
            @submit="onSubmitDialogForm"
            hide-footer
          >
            <template #fields="{ formValues }">
              <slot :key="fieldsKey" :form-values="formValues" name="fields" />
            </template>
          </Form>

          <slot
            :hide="() => (showConfirm = false)"
            :submit="onFormValidated"
            :visible="showConfirm"
            name="confirm"
          />

          <div v-bind="DialogFormPreset?.footer">
            <Checkbox
              v-if="showStayCheckbox"
              v-bind="DialogFormPreset?.staycheckbox"
              :label="stayCheckboxLabel ?? locale.stayCheckboxLabel"
              :model-value="form?.stayAfterSubmit"
              @update:model-value="
                form && (form.stayAfterSubmit = $event as boolean)
              "
            />

            <ValidatorMessage
              v-show="invalid && validatorMessage"
              v-bind="DialogFormPreset?.validatormessage"
              :format="false"
              :message="validatorMessage"
            />

            <div
              v-if="props.buttonsTemplate.length || !!slots.actionButtons"
              v-bind="DialogFormPreset?.footerbutton"
            >
              <slot :submit="onButtonSubmitClicked" name="actionButtons">
                <Button
                  v-if="props.buttonsTemplate?.includes('cancel')"
                  v-bind="DialogFormPreset?.cancelbtn"
                  :label="cancelBtnLabel ?? locale.cancelBtnLabel"
                  @click="emit('close'), emit('update:visible', false)"
                  severity="secondary"
                  text
                />

                <Button
                  v-if="props.buttonsTemplate?.includes('clear')"
                  v-bind="DialogFormPreset?.clearfield"
                  :label="clearBtnLabel ?? locale.clearBtnLabel"
                  @click="clearField"
                  class="whitespace-nowrap"
                  text
                />

                <Button
                  v-if="props.buttonsTemplate?.includes('submit')"
                  v-bind="DialogFormPreset?.savesubmitbutton"
                  :label="submitBtnLabel ?? locale.submitBtnLabel"
                  :severity="
                    props.severity === 'primary'
                      ? undefined
                      : (props.severity ?? 'success')
                  "
                  @click="onButtonSubmitClicked"
                />
              </slot>
            </div>
          </div>
        </section>

        <aside
          v-if="$slots['aside-right']"
          v-bind="DialogFormPreset?.asidesection(expanded, asideRightWidth)"
        >
          <slot name="aside-right" />
        </aside>
      </div>
    </template>
  </Dialog>
</template>
